/**
 * Supabase Realtime subscriptions for the manager.
 *
 * Replaces polling with WebSocket subscriptions:
 * - direct_chat_messages INSERT → instant chat delivery (~100ms vs 2s)
 * - agent_doc_versions INSERT → instant drift detection (~100ms vs 5min)
 * - host_agents INSERT/DELETE → instant agent assignment changes
 *
 * Falls back to polling if the Realtime connection drops.
 */

import { createClient, type SupabaseClient, type RealtimeChannel } from '@supabase/supabase-js';
import { formatActorId, isSelfCompletion, type KanbanCompletionEvent } from '@augmented/core';

// ---------------------------------------------------------------------------
// Shared client
// ---------------------------------------------------------------------------

let client: SupabaseClient | null = null;
let chatChannel: RealtimeChannel | null = null;
let driftChannel: RealtimeChannel | null = null;
let assignChannel: RealtimeChannel | null = null;
let configChannel: RealtimeChannel | null = null;
let kanbanChannel: RealtimeChannel | null = null;
let integrationContextChannel: RealtimeChannel | null = null;
// ENG-4536: one channel per agent — the token-rotation topic is per-agent, so
// there is no single filtered channel to hold like the others above.
let tokenRotationChannels: RealtimeChannel[] = [];
/**
 * ENG-4536 (CodeRabbit) — which subscribe pass owns the current channels.
 *
 * `unsubscribe()` makes Supabase deliver `CLOSED` through the callback the old
 * channel still holds. Since `startRealtimeTokenRotation` tears down before it
 * rebuilds, every re-subscribe fired a "subscription lost" from the channel it
 * had just deliberately closed — the manager cleared its snapshot, the next tick
 * re-subscribed, and that closed a channel again. A permanent rebind loop, built
 * out of two fixes that were each correct alone.
 *
 * Every pass captures this number; a status callback whose epoch is stale is
 * from a channel nobody is listening to any more and is dropped.
 */
let tokenRotationEpoch = 0;
let connected = false;
let tearingDown = false; // Guard against re-entrant teardown

export interface RealtimeConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  token: string;
  log: (msg: string) => void;
}

function ensureClient(config: RealtimeConfig): SupabaseClient {
  if (client) return client;

  client = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    global: {
      headers: { Authorization: `Bearer ${config.token}` },
    },
    realtime: {
      params: { apikey: config.supabaseAnonKey },
    },
  });

  client.realtime.setAuth(config.token);
  return client;
}

// ---------------------------------------------------------------------------
// Direct chat subscription
// ---------------------------------------------------------------------------

export interface ChatMessagePayload {
  id: string;
  agent_id: string;
  session_id: string;
  content: string;
  status: string;
  created_at: string;
}

export function startRealtimeChat(
  config: RealtimeConfig & {
    agentIds: string[];
    onMessage: (msg: ChatMessagePayload) => void;
    onError: (err: Error) => void;
    onStatusChange: (status: 'connected' | 'disconnected' | 'error') => void;
  },
): void {
  const { agentIds, onMessage, onStatusChange, log } = config;
  if (agentIds.length === 0) return;

  const sb = ensureClient(config);
  const filterStr = agentIds.length === 1
    ? `agent_id=eq.${agentIds[0]}`
    : `agent_id=in.(${agentIds.join(',')})`;

  chatChannel = sb
    .channel('direct-chat-realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'direct_chat_messages',
      filter: filterStr,
    }, (payload) => {
      const msg = payload.new as ChatMessagePayload;
      if (msg.status !== 'pending') return;
      log(`[realtime] Chat message for agent ${msg.agent_id}: id=${msg.id}`);
      onMessage(msg);
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        connected = true;
        log('[realtime] Chat channel connected');
        onStatusChange('connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        if (tearingDown) return; // Prevent re-entrant teardown from recursive close events
        connected = false;
        log(`[realtime] Chat channel: ${status} — will reconnect next cycle`);
        // Don't call stopRealtimeChat here — it triggers more CLOSED events.
        // Just null out references and let the manager reset flags for fresh reconnect.
        chatChannel = null;
        driftChannel = null;
        assignChannel = null;
        configChannel = null;
        kanbanChannel = null;
        integrationContextChannel = null;
        // ENG-4536: an ARRAY, so it must be emptied rather than nulled. Leaving
        // dead channel objects here would let the next subscribe pass append to
        // them, and every rotation would fire one early poll per stale entry.
        tokenRotationChannels = [];
        if (client) { try { client.removeAllChannels(); } catch { /* ignore */ } client = null; }
        onStatusChange(status === 'TIMED_OUT' ? 'error' : 'disconnected');
      }
    });

  log(`[realtime] Subscribing to direct_chat_messages for ${agentIds.length} agent(s)`);
}

// ---------------------------------------------------------------------------
// Drift detection subscription
// ---------------------------------------------------------------------------

export interface DriftPayload {
  version_id: string;
  agent_id: string;
  doc_type: string;
  version: string;
  created_at: string;
}

export function startRealtimeDrift(
  config: RealtimeConfig & {
    agentIds: string[];
    onDrift: (payload: DriftPayload) => void;
  },
): void {
  const { agentIds, onDrift, log } = config;
  if (agentIds.length === 0) return;

  const sb = ensureClient(config);
  const filterStr = agentIds.length === 1
    ? `agent_id=eq.${agentIds[0]}`
    : `agent_id=in.(${agentIds.join(',')})`;

  driftChannel = sb
    .channel('drift-realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'agent_doc_versions',
      filter: filterStr,
    }, (payload) => {
      const doc = payload.new as DriftPayload;
      log(`[realtime] Doc version change for agent ${doc.agent_id}: ${doc.doc_type} v${doc.version}`);
      onDrift(doc);
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        log('[realtime] Drift channel connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        log(`[realtime] Drift channel: ${status}`);
      }
    });

  log(`[realtime] Subscribing to agent_doc_versions for ${agentIds.length} agent(s)`);
}

// ---------------------------------------------------------------------------
// Agent assignment subscription
// ---------------------------------------------------------------------------

export interface AssignmentPayload {
  host_id: string;
  agent_id: string;
  assigned_by: string;
}

/**
 * ENG-7335: per-agent last-seen `restart_requested_at` for the restart doorbell.
 * Its ONLY job is to suppress duplicate/redelivered Realtime UPDATE events for
 * the same value - it deliberately does NOT record "this restart was handled".
 * Authority over "handled" stays with the manager's durable
 * `lastRestartProcessedAt` (persisted to manager-state.json) and the slow-poll
 * backstop: if the lane fails after we fire, the stamp is never advanced and the
 * next poll re-services it. So a stale/lost entry here can only cost one extra
 * (harmless, lane-deduped) fire, never a dropped restart.
 */
const restartRequestFingerprint = new Map<string, string | null>();

export function startRealtimeAssignments(
  config: RealtimeConfig & {
    hostId: string;
    onAssign: (payload: AssignmentPayload) => void;
    onUnassign: (payload: { agent_id: string }) => void;
    // ENG-7335: fired when a host_agents row for THIS host gets a new (non-null,
    // advanced) restart_requested_at - the fast-restart doorbell. Advisory: the
    // manager gates it on the restart-doorbell flag and the slow poll backstops it.
    // ENG-7345: restart_reason discriminates a pure operator restart (NULL /
    // 'manual' -> surgical fast lane) from a config-change reload (any other
    // value -> full poll so .mcp.json re-renders before the respawn).
    onRestartRequested?: (payload: { agent_id: string; restart_requested_at: string; restart_reason: string | null }) => void;
  },
): void {
  const { hostId, onAssign, onUnassign, onRestartRequested, log } = config;

  const sb = ensureClient(config);

  assignChannel = sb
    .channel('assignment-realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'host_agents',
      filter: `host_id=eq.${hostId}`,
    }, (payload) => {
      const row = payload.new as AssignmentPayload;
      log(`[realtime] Agent assigned: ${row.agent_id} to host ${row.host_id}`);
      onAssign(row);
    })
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'host_agents',
      filter: `host_id=eq.${hostId}`,
    }, (payload) => {
      // ENG-7335: restart doorbell. host_agents UPDATEs fire for other columns
      // too (assigned_at, etc.), so filter to a genuine restart signal: a
      // non-null restart_requested_at that differs from the last value we saw
      // for this agent. A null value (never-set or just-acked) updates the
      // fingerprint but never fires. Duplicate redeliveries (same value) are
      // suppressed. The manager's dedup + backstop make an over-fire harmless.
      const row = payload.new as { agent_id?: string; restart_requested_at?: string | null; restart_reason?: string | null };
      const agentId = row.agent_id;
      if (!agentId) return;
      const requestedAt = row.restart_requested_at ?? null;
      const prev = restartRequestFingerprint.get(agentId);
      restartRequestFingerprint.set(agentId, requestedAt);
      if (requestedAt && requestedAt !== prev && onRestartRequested) {
        // ENG-7345: forward restart_reason so the manager can route a
        // config-change reload through a full poll (re-render) vs the surgical
        // fast lane for a pure restart. postgres_changes sends every column, so
        // this is present; an older API that never wrote the column sends
        // undefined -> null -> treated as a pure restart (safe default).
        const restartReason = row.restart_reason ?? null;
        log(`[realtime] Restart requested for ${agentId} at ${requestedAt}${restartReason ? ` (reason=${restartReason})` : ''}`);
        onRestartRequested({ agent_id: agentId, restart_requested_at: requestedAt, restart_reason: restartReason });
      }
    })
    .on('postgres_changes', {
      event: 'DELETE',
      schema: 'public',
      table: 'host_agents',
    }, (payload) => {
      // DELETE events can't be filtered by Supabase Realtime
      // We receive all DELETEs and filter client-side
      const old = payload.old as { host_id?: string; agent_id?: string };
      if (old.host_id === hostId && old.agent_id) {
        log(`[realtime] Agent unassigned: ${old.agent_id} from host ${hostId}`);
        onUnassign({ agent_id: old.agent_id });
      }
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        log('[realtime] Assignment channel connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        log(`[realtime] Assignment channel: ${status}`);
      }
    });

  log(`[realtime] Subscribing to host_agents for host ${hostId}`);
}

// ---------------------------------------------------------------------------
// Agent config change subscription
// ---------------------------------------------------------------------------

export interface AgentConfigPayload {
  agent_id: string;
  code_name: string;
  status: string;
  framework: string;
  session_mode: string;
  primary_model: string | null;
  updated_at: string;
}

export function startRealtimeConfig(
  config: RealtimeConfig & {
    agentIds: string[];
    onConfigChange: (payload: AgentConfigPayload) => void;
  },
): void {
  const { agentIds, onConfigChange, log } = config;
  if (agentIds.length === 0) return;

  const sb = ensureClient(config);
  const filterStr = agentIds.length === 1
    ? `agent_id=eq.${agentIds[0]}`
    : `agent_id=in.(${agentIds.join(',')})`;

  // Track last known values to filter out timestamp-only updates
  const lastKnown = new Map<string, string>();

  configChannel = sb
    .channel('config-realtime')
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'agents',
      filter: filterStr,
    }, (payload) => {
      const agent = payload.new as AgentConfigPayload;

      // Build a fingerprint of meaningful fields (ignore timestamps like updated_at, last_heartbeat_at)
      const fingerprint = `${agent.status}|${agent.framework}|${agent.session_mode}|${agent.primary_model}`;
      const prev = lastKnown.get(agent.agent_id);

      if (prev === fingerprint) return; // timestamp-only change, ignore
      lastKnown.set(agent.agent_id, fingerprint);

      log(`[realtime] Agent config changed: ${agent.code_name} (status=${agent.status})`);
      onConfigChange(agent);
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        log('[realtime] Config channel connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        log(`[realtime] Config channel: ${status}`);
      }
    });

  log(`[realtime] Subscribing to agents table for ${agentIds.length} agent(s)`);
}

// ---------------------------------------------------------------------------
// Kanban item subscription — trigger work when items hit 'todo'
// ---------------------------------------------------------------------------

export interface KanbanItemPayload {
  id: string;
  agent_id: string;
  title: string;
  status: string;
  priority: number;
  // ENG-5599: present on the realtime row (postgres_changes sends every
  // column). Lets onTodayItem skip cards a dedicated path already nudged for —
  // e.g. scheduled-task cards driven by routeScheduledTaskViaKanban — so the
  // generic kanban-work trigger doesn't double-nudge them.
  source_type?: string | null;
  last_actor_id?: string | null;
  completed_at?: string | null;
}

// ---------------------------------------------------------------------------
// Kanban delivery telemetry (ENG-4514)
//
// Tracks DB→manager latency for completion events and how many we suppress
// (self-actor) or drop (subscriber error). Surfaced via getKanbanMetrics()
// so an operator-facing status command can render p50/p95 + counters
// without having to scrape logs.
// ---------------------------------------------------------------------------

interface KanbanMetrics {
  delivered: number;
  suppressedSelfActor: number;
  dropped: number;
  /** Last 200 latencies (ms) — bounded ring; old samples roll off. */
  latencies: number[];
}

const METRICS_WINDOW = 200;
const kanbanMetrics: KanbanMetrics = {
  delivered: 0,
  suppressedSelfActor: 0,
  dropped: 0,
  latencies: [],
};

function recordLatency(ms: number): void {
  kanbanMetrics.latencies.push(ms);
  if (kanbanMetrics.latencies.length > METRICS_WINDOW) {
    kanbanMetrics.latencies.shift();
  }
}

/**
 * Visible for testing — nearest-rank percentile on a pre-sorted array.
 * Returns 0 when the array is empty so callers don't need a guard.
 */
export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p));
  return sorted[idx] ?? 0;
}

export interface KanbanMetricsSnapshot {
  delivered: number;
  suppressedSelfActor: number;
  dropped: number;
  sampleSize: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
}

export function getKanbanMetrics(): KanbanMetricsSnapshot {
  const sorted = [...kanbanMetrics.latencies].sort((a, b) => a - b);
  return {
    delivered: kanbanMetrics.delivered,
    suppressedSelfActor: kanbanMetrics.suppressedSelfActor,
    dropped: kanbanMetrics.dropped,
    sampleSize: sorted.length,
    p50LatencyMs: percentile(sorted, 0.5),
    p95LatencyMs: percentile(sorted, 0.95),
  };
}

/**
 * Visible for testing — reset the in-memory counters between runs.
 */
export function resetKanbanMetrics(): void {
  kanbanMetrics.delivered = 0;
  kanbanMetrics.suppressedSelfActor = 0;
  kanbanMetrics.dropped = 0;
  kanbanMetrics.latencies.length = 0;
}

export function startRealtimeKanban(
  config: RealtimeConfig & {
    agentIds: string[];
    onTodayItem: (payload: KanbanItemPayload) => void;
    /**
     * ENG-4514: invoked when a kanban item transitions to `done` or
     * `failed` and the actor was NOT the agent itself. Forwarding into
     * the agent runtime (Claude Code MCP surface) lands in ENG-4515.
     */
    onCompletion?: (event: KanbanCompletionEvent) => void;
  },
): void {
  const { agentIds, onTodayItem, onCompletion, log } = config;
  if (agentIds.length === 0) return;

  const sb = ensureClient(config);
  const filterStr = agentIds.length === 1
    ? `agent_id=eq.${agentIds[0]}`
    : `agent_id=in.(${agentIds.join(',')})`;

  kanbanChannel = sb
    .channel('kanban-realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'agent_kanban_items',
      filter: filterStr,
    }, (payload) => {
      const item = payload.new as KanbanItemPayload;
      if (item.status === 'todo') {
        // Don't log raw titles — task content can be sensitive. Identifiers only.
        log(`[realtime] New kanban item in 'todo': item_id=${item.id} agent=${item.agent_id}`);
        onTodayItem(item);
      }
    })
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'agent_kanban_items',
      filter: filterStr,
    }, (payload) => {
      const item = payload.new as KanbanItemPayload;
      const old = payload.old as KanbanItemPayload;
      // Only trigger when status changes TO 'todo' (not from todo to something else)
      if (item.status === 'todo' && old.status !== 'todo') {
        log(`[realtime] Kanban item moved to 'todo': item_id=${item.id} agent=${item.agent_id}`);
        onTodayItem(item);
      }
      // ENG-4514: completion events. Fire only on the *transition* into
      // done/failed so we don't double-emit when a downstream UPDATE (e.g. an
      // embedding write) touches an already-completed row.
      if (
        onCompletion &&
        (item.status === 'done' || item.status === 'failed') &&
        old.status !== item.status
      ) {
        const event: KanbanCompletionEvent = {
          agent_id: item.agent_id,
          item_id: item.id,
          status: item.status,
          last_actor_id: item.last_actor_id ?? null,
          completed_at: item.completed_at ?? null,
          title: item.title,
        };
        if (isSelfCompletion(event)) {
          kanbanMetrics.suppressedSelfActor++;
          // Don't log per-event for the suppressed path — the agent's own
          // kanban_done log already covered it. Counter is enough.
          return;
        }
        // Latency is best-effort: postgres_changes payloads carry
        // `commit_timestamp`; subtract from now() for an approximate
        // DB→manager delivery time. Falls back to 0 if missing.
        const commitTs = (payload as unknown as { commit_timestamp?: string }).commit_timestamp;
        const latencyMs = commitTs ? Math.max(0, Date.now() - new Date(commitTs).getTime()) : 0;
        try {
          // Only count as delivered when the handler returns successfully —
          // otherwise the same event would land in both `delivered` and
          // `dropped`, overstating success.
          onCompletion(event);
          recordLatency(latencyMs);
          kanbanMetrics.delivered++;
          log(
            `[realtime] Kanban completion: agent=${item.agent_id} item=${item.id} ` +
            `status=${item.status} actor=${item.last_actor_id ?? 'unknown'} ` +
            `latency_ms=${latencyMs}`,
          );
        } catch (err) {
          // The handler threw — count as a drop and keep the subscription
          // healthy. Without this guard a runtime adapter bug would knock
          // the manager off the channel for every agent.
          kanbanMetrics.dropped++;
          log(`[realtime] Kanban completion handler error: ${(err as Error).message}`);
        }
      }
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        log('[realtime] Kanban channel connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        log(`[realtime] Kanban channel: ${status}`);
      }
    });

  log(`[realtime] Subscribing to agent_kanban_items for ${agentIds.length} agent(s)`);
  // Suppress unused-import warnings — formatActorId is exported from core
  // for symmetry with isSelfCompletion and used by tests.
  void formatActorId;
}

// ---------------------------------------------------------------------------
// Integration context subscription (ENG-4342)
//
// Subscribes to INSERT/UPDATE on `plugin_context` filtered by the manager's
// agent IDs. Fires `onContextChange(agentId)` whenever a row touches an
// agent we manage. The handler is responsible for triggering an early poll
// or re-rendering — this module just delivers the event.
// ---------------------------------------------------------------------------

export interface IntegrationContextChangePayload {
  agent_id: string;
  plugin_id: string;
}

export function startRealtimeIntegrationContext(
  config: RealtimeConfig & {
    agentIds: string[];
    onContextChange: (payload: IntegrationContextChangePayload) => void;
  },
): void {
  const { agentIds, onContextChange, log } = config;
  if (agentIds.length === 0) return;

  const sb = ensureClient(config);
  const filterStr = agentIds.length === 1
    ? `agent_id=eq.${agentIds[0]}`
    : `agent_id=in.(${agentIds.join(',')})`;

  // ENG-6009 Phase 4: the table was renamed plugin_context → agent_integration_context.
  // wal2json decodes events from the base table by name string (NOT the OID),
  // so subscriptions filtered by the old name go silent post-rename. Dual-
  // subscribe to BOTH names for one release cycle — the API's compat VIEW on
  // the old prefix routes inserts/updates through to the new base table, so
  // the new-name handler fires on every event; the old-name handler is dead
  // weight after the rename and removed in a follow-up CLI release.
  integrationContextChannel = sb
    .channel('agent-integration-context-realtime')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'agent_integration_context',
      filter: filterStr,
    }, (payload) => {
      const row = payload.new as IntegrationContextChangePayload;
      log(`[realtime] agent_integration_context INSERT for agent ${row.agent_id} (plugin ${row.plugin_id})`);
      onContextChange(row);
    })
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'agent_integration_context',
      filter: filterStr,
    }, (payload) => {
      const row = payload.new as IntegrationContextChangePayload;
      log(`[realtime] agent_integration_context UPDATE for agent ${row.agent_id} (plugin ${row.plugin_id})`);
      onContextChange(row);
    })
    // Legacy table-name fallback — keeps pre-Phase-4 stages working through
    // the rollout. wal2json decodes the BASE table's events even though
    // SELECTs hit the view, so the old-name subscription is only useful
    // when the actual base table is still named plugin_context (i.e., a
    // stage where this migration hasn't applied yet). Safe to drop after
    // every stage has the rename live.
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'plugin_context',
      filter: filterStr,
    }, (payload) => {
      const row = payload.new as IntegrationContextChangePayload;
      log(`[realtime] plugin_context (legacy) INSERT for agent ${row.agent_id} (plugin ${row.plugin_id})`);
      onContextChange(row);
    })
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'plugin_context',
      filter: filterStr,
    }, (payload) => {
      const row = payload.new as IntegrationContextChangePayload;
      log(`[realtime] plugin_context (legacy) UPDATE for agent ${row.agent_id} (plugin ${row.plugin_id})`);
      onContextChange(row);
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        log('[realtime] Integration context channel connected');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        log(`[realtime] Integration context channel: ${status}`);
      }
    });

  log(`[realtime] Subscribing to agent_integration_context (+ legacy plugin_context) for ${agentIds.length} agent(s)`);
}

/**
 * Tear down only the integration-context channel — used when the active
 * agent set changes and we need to re-subscribe with an updated filter
 * (ENG-4725). Leaves the rest of the realtime client + other channels
 * untouched.
 */
export function stopRealtimeIntegrationContext(): void {
  if (!integrationContextChannel) return;
  try { integrationContextChannel.unsubscribe(); } catch {}
  integrationContextChannel = null;
}

// ---------------------------------------------------------------------------
// Token rotation subscription (ENG-4536)
//
// The ONE broadcast subscription in this module; everything above is
// `postgres_changes`. That difference is deliberate and is explained in
// `packages/api/src/lib/token-rotation-broadcast.ts`: CDC on the integrations
// tables would ship the encrypted `credentials` column to every subscriber on
// every rotation, and there is no per-agent filter to subscribe with for an
// org- or team-scoped install. The broadcast carries an invalidation hint and
// no credential.
//
// One channel per agent, because the topic is per-agent
// (`token-rotated-agent:{agentId}`) — a shared topic would deliver every
// organization's rotations to every manager.
// ---------------------------------------------------------------------------

/** Mirrors `TokenRotatedPayload` in packages/api/src/lib/token-rotation-broadcast.ts. */
export interface TokenRotatedEvent {
  /**
   * The rotated integration's id. THE field to act on.
   *
   * The server deliberately over-notifies — the audience is every agent under
   * the integration's scope, not the precise effective set — so an agent can
   * receive an event for an install it does not use. That is safe ONLY while
   * consumers key off this id. Reacting to `definition_id` would treat
   * "some Xero token rotated somewhere in the org" as "my Xero token is stale"
   * and throw away a still-valid, differently-scoped credential, which is the
   * wasted refresh this whole feature exists to remove.
   */
  integration_id: string;
  /** Provider definition. Logging and diagnostics only — never a match key. */
  definition_id: string;
  rotated_at: string;
}

export function startRealtimeTokenRotation(
  config: RealtimeConfig & {
    agentIds: string[];
    onTokenRotated: (agentId: string, event: TokenRotatedEvent) => void;
    /**
     * Fired when a channel drops and will not come back on its own.
     *
     * Without it, losing ONLY these channels is invisible and permanent. The
     * manager keys its re-subscribe decision on
     * `subscribedIntegrationContextAgentIds`, which still equals the active
     * set, so `ensureRealtimeIntegrationContextStarted` returns early on every
     * subsequent tick and never rebinds. Invalidation is silently gone for the
     * life of the process — and the most likely cause, the private topic being
     * refused by RLS, produces exactly this shape.
     *
     * It degrades rather than breaks (the scheduled poll and the post-401
     * recovery both still work), which is precisely why it needs to be
     * reported: nothing else would ever surface it.
     */
    onSubscriptionLost?: (agentId: string, status: string) => void;
    /**
     * ENG-9265: fired when a channel reaches SUBSCRIBED.
     *
     * The loss callback above gave the manager a way to notice a drop and
     * nothing at all to notice a RECOVERY, so a rebind that worked and a rebind
     * that failed were indistinguishable from outside. That is fine while the
     * response to a loss is "retry next tick forever" — it stops being fine the
     * moment the retry is bounded, because a bound needs to know when to reset.
     * It is also what lets an alert raised on a stuck rebind ever close.
     */
    onSubscribed?: (agentId: string) => void;
  },
): void {
  const { agentIds, onTokenRotated, onSubscriptionLost, onSubscribed, log } = config;
  if (agentIds.length === 0) return;

  // Every other subscriber in this module holds ONE channel in a single slot,
  // so re-subscribing overwrites and at worst leaks one object. This one holds
  // an array, so a second call without a teardown would ADD a duplicate channel
  // per agent and fire one early poll per duplicate on every rotation. Drop
  // whatever is held before rebuilding, rather than relying on every caller to
  // remember.
  stopRealtimeTokenRotation();

  // Claim this pass. Anything the torn-down channels say from here on is stale.
  const epoch = ++tokenRotationEpoch;

  const sb = ensureClient(config);

  for (const agentId of agentIds) {
    const topic = `token-rotated-agent:${agentId}`;
    const channel = sb
      // `private: true` is required, not cosmetic: the topic is RLS-gated by
      // migration 20260815000001, and subscribing without it is refused.
      .channel(topic, { config: { private: true } })
      .on('broadcast', { event: 'token.rotated' }, (message) => {
        const payload = (message as { payload?: unknown }).payload as Partial<TokenRotatedEvent> | undefined;

        // A broadcast payload is whatever arrived on the wire, so it is
        // validated rather than trusted. Without `integration_id` the event
        // says only "something rotated somewhere", and the one safe response
        // to that is to do nothing — acting on a partial event is how a
        // malformed message becomes an unconditional cache flush.
        if (!payload || typeof payload.integration_id !== 'string' || payload.integration_id.length === 0) {
          log(`[realtime] token.rotated for agent ${agentId} ignored — no integration_id in payload`);
          return;
        }

        log(
          `[realtime] token.rotated for agent ${agentId} (integration ${payload.integration_id}, ` +
          `${payload.definition_id ?? 'unknown provider'})`,
        );

        onTokenRotated(agentId, {
          integration_id: payload.integration_id,
          definition_id: typeof payload.definition_id === 'string' ? payload.definition_id : 'unknown',
          rotated_at: typeof payload.rotated_at === 'string' ? payload.rotated_at : new Date().toISOString(),
        });
      })
      .subscribe((status) => {
        // A status from a superseded pass is not news about the live binding.
        // Without this guard the deliberate teardown at the top of this function
        // reports itself as a loss — see `tokenRotationEpoch`.
        if (epoch !== tokenRotationEpoch) return;
        // TIMED_OUT is included deliberately. The chat channel above treats it
        // as a lost subscription and this one used to omit it, so a channel
        // that timed out stayed silently dead — the same end state as CLOSED,
        // reached by a different door.
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          log(`[realtime] Token rotation channel for agent ${agentId}: ${status}`);
          onSubscriptionLost?.(agentId, status);
          return;
        }
        // ENG-9265: the success edge, reported so a bounded retry can reset and
        // a stuck-rebind alert can close. Silence used to mean both "working"
        // and "wedged".
        if (status === 'SUBSCRIBED') onSubscribed?.(agentId);
      });

    tokenRotationChannels.push(channel);
  }

  log(`[realtime] Subscribing to token.rotated for ${agentIds.length} agent(s)`);
}

/**
 * Tear down only the token-rotation channels — used when the active agent set
 * changes, mirroring `stopRealtimeIntegrationContext`. There is one channel per
 * agent rather than one filtered channel, so ALL of them are dropped and the
 * caller re-subscribes with the new set.
 */
export function stopRealtimeTokenRotation(): void {
  // Retire the epoch BEFORE unsubscribing, so the CLOSED that `unsubscribe()`
  // delivers is already stale by the time the callback sees it. Doing it after
  // would leave a window where a deliberate teardown still reports a loss.
  tokenRotationEpoch += 1;
  for (const channel of tokenRotationChannels) {
    try { channel.unsubscribe(); } catch {}
  }
  tokenRotationChannels = [];
}

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

export function updateRealtimeToken(token: string): void {
  if (client) {
    client.realtime.setAuth(token);
  }
}

export function isRealtimeConnected(): boolean {
  return connected;
}

/**
 * ENG-5614: the *actual* phoenix socket state, distinct from `connected`
 * (our channel-subscription flag set on SUBSCRIBED / cleared on
 * CLOSED·CHANNEL_ERROR·TIMED_OUT). A half-open websocket can leave
 * `connected === true` while the underlying socket is dead — the channel's
 * close callback never fires, so inbound delivery silently stops while
 * `isRealtimeConnected()` still reports healthy. The manager reconciles the
 * two each poll cycle and forces the polling fallback when they diverge.
 * Returns false when there is no client (never started / torn down), which
 * matches `isRealtimeConnected()` so the divergence check stays inert.
 */
export function isRealtimeSocketConnected(): boolean {
  try {
    return client?.realtime?.isConnected() ?? false;
  } catch {
    return false;
  }
}

/**
 * ENG-5614: pure predicate — should the manager force the polling fallback?
 * True ONLY for the half-open divergence: our channel-subscription flag says
 * connected but the underlying socket is actually dead. Extracted as a pure
 * function so the decision is unit-testable without the manager's module
 * state. Note it returns false when both are false (no client) — the normal
 * "not connected, just poll" path, not a divergence.
 */
export function shouldForcePollFallback(
  channelConnected: boolean,
  socketConnected: boolean,
): boolean {
  return channelConnected && !socketConnected;
}

export function stopRealtimeChat(): void {
  if (tearingDown) return;
  tearingDown = true;
  try {
    if (chatChannel) { try { chatChannel.unsubscribe(); } catch {} chatChannel = null; }
    if (driftChannel) { try { driftChannel.unsubscribe(); } catch {} driftChannel = null; }
    if (assignChannel) { try { assignChannel.unsubscribe(); } catch {} assignChannel = null; }
    if (configChannel) { try { configChannel.unsubscribe(); } catch {} configChannel = null; }
    if (kanbanChannel) { try { kanbanChannel.unsubscribe(); } catch {} kanbanChannel = null; }
    if (integrationContextChannel) { try { integrationContextChannel.unsubscribe(); } catch {} integrationContextChannel = null; }
    for (const ch of tokenRotationChannels) { try { ch.unsubscribe(); } catch {} }
    tokenRotationChannels = [];
    if (client) {
      try { client.removeAllChannels(); } catch {}
      client = null;
    }
    connected = false;
  } finally {
    tearingDown = false;
  }
}
